// "What needs someone today" — the ONE counting rule the calendar's summary
// tiles, the Agenda's Overdue heading and the farm Home "Today" strip share.
// Kept pure and beside pageRules.ts so a page never re-derives it (a copy
// that drifts is a Home that says 12 while the calendar says 8).
//
// Counting rule: sum a (day x source) group's `event_count` against its
// `worst_band`. That is exact, not an approximation: `band` is a pure function
// of the event date and `day` is in the RPC's GROUP BY, so every row in a
// group shares one band. The horizon read (pageRules.HORIZON) carries only the
// `due` and `planned` tenses.
import { combineReads, type ReadStateLike, type Staleness } from '@/lib/readState'
import type { CalendarDaySummary } from './useCalendarQuery'

export interface DueCounts { overdue: number; soon: number }

/** Overdue and due-in-7-days over the HORIZON rows (unfiltered by chips). */
export function dueCounts(rows: readonly CalendarDaySummary[]): DueCounts {
  let overdue = 0, soon = 0
  for (const r of rows) {
    if (r.worst_band === 'overdue') overdue += r.event_count
    else if (r.worst_band === 'd7') soon += r.event_count
  }
  return { overdue, soon }
}

/** Events already captured, over the WINDOW rows (the `done` plane). */
export function doneCount(rows: readonly CalendarDaySummary[]): number {
  let done = 0
  for (const r of rows) if (r.tense === 'done') done += r.event_count
  return done
}

/** The Agenda's Overdue heading (FM11 UF-23): the same count the tile shows,
 *  and the days it is spread over. It used to count ROWS (one per day and
 *  source), so a tile reading 12 sat over "Overdue (8)". */
export function overdueHeading(overdueRows: readonly CalendarDaySummary[]): string {
  const events = dueCounts(overdueRows).overdue
  const days = new Set(overdueRows.map(r => r.day)).size
  return `Overdue · ${events} on ${days} ${days === 1 ? 'day' : 'days'}`
}

/** Planned work (spray tasks, fertilising) whose planned date is `day`. */
export function plannedOnDay(rows: readonly CalendarDaySummary[], day: string): number {
  let n = 0
  for (const r of rows) if (r.tense === 'planned' && r.day === day) n += r.event_count
  return n
}

/**
 * Which of the strip's states a set of reads puts it in. Five honest states
 * and the normal one:
 *
 *   loading — a read is still in flight: "Checking today…", never a figure
 *   offline — nothing cached and the device is paused: "Can't check offline"
 *   failed  — nothing held and the read errored: "Couldn't load · Retry"
 *   stale   — data held but offline / the refresh failed: figures + a note
 *   quiet   — every read answered, nothing needs anyone: "Nothing … today"
 *   pills   — every read answered, something needs someone
 *
 * `quiet` is reachable ONLY from a usable, non-stale answer. An empty read
 * that has not answered is never "nothing today".
 */
export type TodayStripState = 'loading' | 'offline' | 'failed' | 'stale' | 'quiet' | 'pills'

export interface TodayStrip { state: TodayStripState; stale: Staleness }

export function todayStripState(reads: readonly ReadStateLike[], pillCount: number): TodayStrip {
  const c = combineReads(...reads)
  if (c.state === 'loading') return { state: 'loading', stale: null }
  if (c.state === 'unread') {
    // Unread = held nothing and either failed or is paused (offline). A paused
    // read is not a failed one: the retry button would do nothing.
    const paused = reads.some(q => q.data === undefined && q.isPaused)
    return { state: paused ? 'offline' : 'failed', stale: null }
  }
  if (c.stale) return { state: 'stale', stale: c.stale }
  return { state: pillCount > 0 ? 'pills' : 'quiet', stale: null }
}
